import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Play, Square } from "lucide-react";
import {
  collectAttachmentMediaPaths,
  canUseDirectMediaSrc,
  clearAttachmentMediaBlock,
  fileDownloadPathsForId,
  getCachedBlobObjectUrl,
  getCachedVideoBlobObjectUrl,
  invalidateBlobCacheEntry,
  resolveAbsoluteMediaHref,
  videoFileCacheKey,
} from "@sd/lib/attachmentMediaUrl";
import { cn } from "@/lib/utils";

function formatMediaTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

function normalizeMediaPath(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return "";
  }
  if (trimmed.startsWith("http://")) {
    return trimmed.replace("http://", "https://");
  }
  return trimmed;
}

type AuthenticatedMediaImageProps = {
  filePath: string;
  /** Extra paths to try when the primary URL fails (thumbnail, download URL, etc.). */
  fallbackPaths?: string[];
  /** file-utility UUID — adds reliable download path candidates. */
  fileId?: string;
  alt: string;
  style?: CSSProperties;
  className?: string;
};

/** Loads file-utility media with API auth (same approach as service-desk ticket attachments). */
export function AuthenticatedMediaImage({
  filePath,
  fallbackPaths = [],
  fileId,
  alt,
  style,
  className,
}: AuthenticatedMediaImageProps) {
  const candidates = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const push = (raw: string) => {
      const normalized = normalizeMediaPath(String(raw ?? ""));
      if (normalized && !seen.has(normalized)) {
        seen.add(normalized);
        out.push(normalized);
      }
    };
    push(filePath);
    for (const raw of fallbackPaths) {
      push(raw);
    }
    if (fileId?.trim()) {
      for (const p of fileDownloadPathsForId(fileId)) {
        push(p);
      }
    }
    return out;
  }, [fileId, filePath, fallbackPaths]);

  const [activeIndex, setActiveIndex] = useState(0);
  const primary = candidates[activeIndex] ?? "";
  const cacheKey = candidates.join("|");
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setActiveIndex(0);
  }, [cacheKey]);

  useEffect(() => {
    if (!primary) {
      setSrc("");
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setSrc(null);

    void (async () => {
      const remaining = candidates.slice(activeIndex);
      const blobUrl = await getCachedBlobObjectUrl(remaining, `${cacheKey}:${activeIndex}`);
      if (cancelled) {
        return;
      }
      if (blobUrl) {
        setSrc(blobUrl);
        setLoading(false);
        return;
      }
      if (activeIndex + 1 < candidates.length) {
        setActiveIndex(index => index + 1);
        return;
      }
      if (canUseDirectMediaSrc(primary)) {
        setSrc(resolveAbsoluteMediaHref(primary));
      } else {
        setSrc("");
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [activeIndex, cacheKey, primary]);

  const handleError = () => {
    if (activeIndex + 1 < candidates.length) {
      setActiveIndex(index => index + 1);
    }
  };

  if (!primary) {
    return null;
  }

  if (loading) {
    const loadingBg =
      typeof style?.background === "string" && style.background
        ? style.background
        : "#fff";
    return (
      <div
        className={cn("animate-pulse rounded-md", className)}
        style={{ ...style, background: loadingBg }}
        aria-hidden
      />
    );
  }

  if (!src) {
    const handleRetry = () => {
      if (fileId?.trim()) {
        clearAttachmentMediaBlock(fileId);
        invalidateBlobCacheEntry(`${cacheKey}:0`);
      }
      setActiveIndex(0);
      setLoading(true);
      setSrc(null);
    };

    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-2 rounded-md bg-muted px-3 py-4 text-xs text-muted-foreground",
          className
        )}
        style={style}
        title="File not available"
      >
        <span>Unavailable</span>
        {fileId ? (
          <button
            type="button"
            className="text-primary underline-offset-2 hover:underline"
            onClick={handleRetry}
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <img
      src={src ?? undefined}
      alt={alt}
      style={style}
      className={className}
      onError={handleError}
    />
  );
}

type AuthenticatedMediaVideoProps = {
  filePath: string;
  fallbackPaths?: string[];
  filenameHint?: string;
  /** file-utility UUID — most reliable download path for edit forms. */
  fileId?: string;
  style?: CSSProperties;
  className?: string;
  /** Show explicit Play / Stop bar on the preview (better for recordings). */
  showPlayStopBar?: boolean;
};

/** Loads file-utility video with API auth (required for edit-ticket preview). */
export function AuthenticatedMediaVideo({
  filePath,
  fallbackPaths = [],
  filenameHint,
  fileId,
  style,
  className,
  showPlayStopBar = false,
}: AuthenticatedMediaVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const candidates = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const push = (raw: string) => {
      const normalized = normalizeMediaPath(String(raw ?? ""));
      if (normalized && !seen.has(normalized)) {
        seen.add(normalized);
        out.push(normalized);
      }
    };
    push(filePath);
    for (const raw of fallbackPaths) {
      push(raw);
    }
    if (fileId?.trim()) {
      for (const p of fileDownloadPathsForId(fileId)) {
        push(p);
      }
    }
    return out;
  }, [fileId, filePath, fallbackPaths]);

  const [activeIndex, setActiveIndex] = useState(0);
  const primary = candidates[activeIndex] ?? "";
  const stableKey = fileId?.trim() || candidates.join("|");
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const candidatesRef = useRef(candidates);
  candidatesRef.current = candidates;
  const lastStableKeyRef = useRef(stableKey);

  useEffect(() => {
    if (lastStableKeyRef.current === stableKey) {
      return;
    }
    lastStableKeyRef.current = stableKey;
    setActiveIndex(0);
    setFailed(false);
    setSrc(null);
    setLoading(true);
  }, [stableKey]);

  useEffect(() => {
    if (!primary) {
      setLoading(false);
      setFailed(true);
      setSrc(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setFailed(false);

    void (async () => {
      const latestCandidates = candidatesRef.current;
      const remaining = latestCandidates.slice(activeIndex);
      const blobUrl = await getCachedVideoBlobObjectUrl(
        remaining,
        `${stableKey}:${activeIndex}`,
        undefined,
        filenameHint,
        fileId
      );
      if (cancelled) {
        return;
      }
      if (blobUrl) {
        setSrc(blobUrl);
        setLoading(false);
        return;
      }
      if (activeIndex + 1 < latestCandidates.length) {
        setActiveIndex(index => index + 1);
        return;
      }
      const directUrl = latestCandidates.find(c => canUseDirectMediaSrc(c));
      if (directUrl) {
        setSrc(resolveAbsoluteMediaHref(directUrl));
        setLoading(false);
        return;
      }
      setFailed(true);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, stableKey, primary, filenameHint, fileId]);

  const handleError = () => {
    invalidateBlobCacheEntry(`video:${stableKey}:${activeIndex}`);
    if (fileId?.trim()) {
      invalidateBlobCacheEntry(videoFileCacheKey(fileId));
    }
    const latestCandidates = candidatesRef.current;
    if (activeIndex + 1 < latestCandidates.length) {
      setActiveIndex(index => index + 1);
      setFailed(false);
      setSrc(null);
      setLoading(true);
      return;
    }
    setFailed(true);
    setSrc(null);
    setLoading(false);
  };

  const handleRetry = () => {
    invalidateBlobCacheEntry(`video:${stableKey}:0`);
    if (fileId?.trim()) {
      invalidateBlobCacheEntry(videoFileCacheKey(fileId));
      clearAttachmentMediaBlock(fileId);
    }
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setLoading(true);
    setFailed(false);
    setActiveIndex(0);
    setSrc(null);
  };

  const handlePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    void video.play().catch(() => {
      setPlaying(false);
    });
  };

  const handleStop = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
    setPlaying(false);
    setCurrentTime(0);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(video.currentTime);
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;
    setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    setCurrentTime(video.currentTime);
  };

  if (!primary) {
    return null;
  }

  if (loading && !src) {
    return (
      <div
        className={cn(
          "flex h-full min-h-[120px] flex-col items-center justify-center gap-2 overflow-hidden rounded-md bg-muted text-sm text-muted-foreground",
          className
        )}
        style={style}
        aria-busy
      >
        <div className="h-8 w-8 animate-pulse rounded-full bg-muted-foreground/20" />
        <span>Loading video…</span>
      </div>
    );
  }

  if (failed || !src) {
    return (
      <div
        className={cn(
          "flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-md bg-muted px-4 text-center text-sm text-muted-foreground",
          className
        )}
        style={style}
      >
        <span>Video could not be loaded.</span>
        <button
          type="button"
          className="text-primary underline-offset-2 hover:underline"
          onClick={handleRetry}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative flex min-h-0 w-full items-center justify-center overflow-hidden bg-black",
        className
      )}
      style={style}
    >
      <video
        ref={videoRef}
        src={src}
        controls={!showPlayStopBar}
        preload="metadata"
        playsInline
        className="block max-h-full max-w-full object-contain"
        onError={handleError}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrentTime(0);
        }}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
      >
        Your browser does not support the video tag.
      </video>
      {showPlayStopBar ? (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent px-2.5 pb-2 pt-8">
          <div
            className="mb-2 h-1 overflow-hidden rounded-full bg-white/20"
            aria-hidden
          >
            <div
              className="h-full rounded-full bg-white/90 transition-[width] duration-75 ease-linear"
              style={{
                width:
                  duration > 0
                    ? `${Math.min(100, (currentTime / duration) * 100)}%`
                    : "0%",
              }}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/20 disabled:cursor-default disabled:opacity-45"
              onClick={handlePlay}
              disabled={playing}
              aria-label="Play video"
            >
              <Play className="h-3.5 w-3.5 fill-current" strokeWidth={0} />
            </button>
            <button
              type="button"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/20"
              onClick={handleStop}
              aria-label="Stop video"
            >
              <Square className="h-3 w-3 fill-current" strokeWidth={0} />
            </button>
            <span className="ml-auto font-mono text-[11px] tabular-nums tracking-wide text-white/90">
              {formatMediaTime(currentTime)} / {formatMediaTime(duration)}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export async function openAuthenticatedMediaPreview(
  rawPath: string,
  fallbackPaths: string[] = []
): Promise<void> {
  const candidates = [rawPath, ...fallbackPaths]
    .map(normalizeMediaPath)
    .filter(Boolean);
  if (!candidates.length) {
    return;
  }
  const blobUrl = await getCachedBlobObjectUrl(candidates, candidates.join("|"));
  const first = candidates[0] ?? "";
  const url =
    blobUrl ??
    (canUseDirectMediaSrc(first) ? resolveAbsoluteMediaHref(first) : "");
  if (!url) {
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
  if (blobUrl) {
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  }
}

export function resolveDisplayMediaUrl(rawPath: string): string {
  return resolveAbsoluteMediaHref(normalizeMediaPath(rawPath));
}

export { collectAttachmentMediaPaths };
